import type { FC, ReactNode } from 'react';

import { useMediaQuery } from '@react-hookz/web';
import type { FormikProps } from 'formik';
import { Form } from 'formik';

import CapsuleIcon from 'src/app/components/Elements/CapsuleIcon';
import { Card } from 'src/app/components/Elements/Card';
import type { TypographyVariant } from 'src/app/components/Elements/Typography';
import { Typography } from 'src/app/components/Elements/Typography';
import GenericError from 'src/app/components/Error/Generic';
import TextInput from 'src/app/components/Form/TextInput';
import { CAPSULE_ICON_STRINGS } from 'src/consts';
import { theme } from 'src/styles/theme';

import EHRInfoCard from '../EHRInfoCard';
import NotEligibleCard from '../NotEligibleCard';

import { ZipCheckStatus } from './constants';
import type { PropsFromRedux } from './container';
import { ZipDeliverableContent } from './DeliverableContent';
import { CardContent, FadeInText, InlineBlockContent, ZipCheckButton, ZipCheckWrapper } from './styles';

type FormValues = {
  zipcode: string;
};

interface ZipCodeDeliverableCheckProps extends PropsFromRedux {
  isProvider: boolean;
  successCTAText?: string;
  successBody?: ReactNode;
  successCTAOnClick?: () => void;
  CTATheme?: 'capsuleGreen60';
  showDeliverToWorkOption?: boolean;
}

export const ZipCodeDeliverableCheckComponent: FC<ZipCodeDeliverableCheckProps & FormikProps<FormValues>> = ({
  values,
  errors,
  status = {},
  isSubmitting,
  setFieldValue,
  setStatus,
  onRedirectToOnboarding,
  successBody,
  successCTAText = 'Get Started',
  successCTAOnClick,
  isProvider = false,
  CTATheme,
  showDeliverToWorkOption = true,
}) => {
  const zipCodeMask = '99999';
  const isMobile = useMediaQuery(theme.breakpoints.smallDown);
  const clearZipCheckStatus = () => {
    setStatus({ isDeliverable: ZipCheckStatus.default, lastCheckedZip: '' });
    setFieldValue('zipcode', '');
  };

  let headerSize: TypographyVariant = 'displayXXL';
  if (isMobile) headerSize = 'displayXL';

  const isNotDeliverable = status.isDeliverable === ZipCheckStatus.isNotDeliverable;
  const isDeliverable = status.isDeliverable === ZipCheckStatus.isDeliverable;
  const isDefault = status.isDeliverable === ZipCheckStatus.default;
  if (!isProvider && isDeliverable) {
    return (
      <ZipDeliverableContent
        clearZipCheckStatus={clearZipCheckStatus}
        headerSize={headerSize}
        successBody={successBody}
        lastCheckedZip={status.lastCheckedZip}
        successCTAOnClick={successCTAOnClick}
        onRedirectToOnboarding={onRedirectToOnboarding}
        successCTAText={successCTAText}
        CTATheme={CTATheme}
      />
    );
  }

  return (
    <>
      {!isProvider && (
        <>
          <Typography variant={'overline'} color={'white'} marginBottom={'s5'}>
            Delivery Checker
          </Typography>
          {isNotDeliverable && (
            <>
              <Typography variant={headerSize} color={'white'} marginBottom={'s5'}>
                <FadeInText>
                  We’re not yet <InlineBlockContent>in {status.lastCheckedZip}.</InlineBlockContent>
                </FadeInText>
              </Typography>
              <Typography variant={'body'} color={'white'} marginBottom={'s7'}>
                <FadeInText>
                  Is there another zip code you would like <InlineBlockContent>to try?</InlineBlockContent>
                </FadeInText>
              </Typography>
            </>
          )}
          {isDefault && (
            <Typography variant={headerSize} color={'white'} marginBottom={'s7'}>
              What is your delivery <InlineBlockContent>zip code?</InlineBlockContent>
            </Typography>
          )}
        </>
      )}
      {isProvider && (
        <Typography variant={headerSize} color={'white'} marginBottom={'s7'}>
          Enter a patient's zip code
        </Typography>
      )}
      <Form>
        <ZipCheckWrapper largerWidth={isProvider}>
          <TextInput
            name="zipcode"
            type="text"
            label="Zip code (e.g., home or work)"
            maskChar={null}
            mask={zipCodeMask}
            mediumContrast
            hasWhiteTextOnly
            isLabelAnimated={true}
            value={values.zipcode}
            hasLightRedError
          />
          <ZipCheckButton bgColor={CTATheme} type="submit" style="primary" size={'medium'} disabled={isSubmitting}>
            Check
          </ZipCheckButton>
        </ZipCheckWrapper>
        <ZipCheckWrapper largerWidth={isProvider}>
          <GenericError errors={errors} />
        </ZipCheckWrapper>
        <ZipCheckWrapper marginTop={'s5'} largerWidth={isProvider}>
          {!isProvider && isNotDeliverable && showDeliverToWorkOption && (
            <Card style={'green'}>
              <CardContent>
                <CapsuleIcon iconString={CAPSULE_ICON_STRINGS.old_pharmacy} block />
                <Typography variant={'overline'} color={'capsuleGreen50'}>
                  Capsule can Also deliver to your work
                </Typography>
              </CardContent>
            </Card>
          )}
          {isProvider && (
            <>
              {isDeliverable && (
                <EHRInfoCard facility={status.facility} clearZipField={() => setFieldValue('zipcode', '')} />
              )}
              {isNotDeliverable && <NotEligibleCard />}
            </>
          )}
        </ZipCheckWrapper>
      </Form>
    </>
  );
};
